import { computed, shallowRef } from 'vue';

/**
 * Light, dark, or whatever the machine says.
 *
 * Three states rather than two: "system" has to be reachable, or a reader who
 * tries the toggle can never get back to following their machine — and the
 * machine is right most of the time, which is why it is the default.
 *
 * Stored in `localStorage` because it is a display preference belonging to a
 * device rather than to an account: the same person on a laptop in a dark room
 * and a desktop by a window wants different answers, and a server round trip
 * would make the page flash the wrong way first.
 */

export type Theme = 'light' | 'dark' | 'system';

const STORAGE_KEY = 'dashboard.theme';

function isTheme(value: unknown): value is Theme {
	return value === 'light' || value === 'dark' || value === 'system';
}

function read(): Theme {
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return isTheme(stored) ? stored : 'system';
	} catch {
		// Private mode, or storage disabled. Following the machine is the
		// right answer when the preference cannot be read.
		return 'system';
	}
}

const current = shallowRef<Theme>('system');

export const theme = computed<Theme>(() => current.value);

/**
 * `system` removes the attribute rather than writing one, so the media query
 * in the stylesheet applies again — the absence is what means "follow".
 */
function apply(value: Theme): void {
	const root = document.documentElement;
	if (value === 'system') root.removeAttribute('data-theme');
	else root.setAttribute('data-theme', value);
}

export function setTheme(value: Theme): void {
	current.value = value;
	apply(value);
	try {
		if (value === 'system') localStorage.removeItem(STORAGE_KEY);
		else localStorage.setItem(STORAGE_KEY, value);
	} catch {
		// The choice still applies to this page; it just will not survive a
		// reload, which is better than refusing to change at all.
	}
}

/** Called once at startup, before the first paint where possible. */
export function initTheme(): void {
	const stored = read();
	current.value = stored;
	apply(stored);
}
